import { ActiveMeetingBroadcast, ScreenshareDetails, StageInvitationBasicInfo } from 'domain/Broadcast';
import { Timestamp } from 'domain/Common';
import { EventMetricsPayload, EventRoom, EventRoomAttendee, MeetingGuest } from 'domain/event';

import type { QuestionAssignmentWSPayload } from 'question/state/types';

export enum EventPhase {
  WaitingRoom = 'WAITING_ROOM',
  Broadcast = 'BROADCAST',
  Nattering = 'NATTERING',
  Ended = 'ENDED',
}

export enum NatteringPhase {
  WaitingToStart = 'WAITING_TO_START',
  InProgress = 'IN_PROGRESS',
}

export enum NatteringStatus {
  Initial = 'INITIAL',
  ToBeDecided = 'TO_BE_DECIDED',
  Accepted = 'ACCEPTED',
  Skipped = 'SKIPPED',
}

export interface ActiveRoomState {
  id: string;
  isBroadcastCountdown: boolean;
  joinedAsSupport: boolean;
  showTranscriptionModal: boolean;
  isTranscribing: boolean;
}

export type AudienceCountUpdatedPayload = {
  audienceCount: number;
};

export interface EventState {
  activeEventId?: number;
  broadcast: ActiveMeetingBroadcast | null;
  /**
   * Tracks whether broadcast recording is armed, independently of whether `broadcast` is currently
   * set. `recording_started` and `broadcast_started` arrive on different STOMP destinations with no
   * ordering guarantee; the host (present when auto-record starts at broadcast open) can receive
   * `recording_started` before `broadcast` exists, which the `broadcast.isRecording` write would
   * otherwise drop. `broadcast_started` reconciles its `isRecording` seed against this flag so the
   * signal is never lost or clobbered. Reset on recording/broadcast stop so it never leaks into the
   * next segment or backstage. See HNS-105.
   */
  recordingArmed: boolean;
  audience: MeetingGuest[];
  audienceCount: number;
  speakers: MeetingGuest[];
  serverTimeOffset: number;
  activeRoom: ActiveRoomState | null;
  rooms: EventRoom[];
  displayRoomHelpCenter: boolean;
  roomsRequestingSupport: string[];
  roomsCount: number;
  pin: string;
  screensharingDetails: ScreenshareDetails | null;
  isScreensharing: boolean;
  metrics: EventMetricsPayload;
  stageInvitation: StageInvitationBasicInfo | null;
  lastCallGuests: EventRoomAttendee[];
  isJoiningNattering: boolean;
  natteringRoundStartTime: Timestamp | undefined;
  natteringRoundEndTime: Timestamp | undefined;
  natteringRoundTopic: string | undefined;
  // 1-indexed id of the current nattering round (from the backend); first round of the event is 1.
  natteringRoundId: number | undefined;
  isBigMeetingMode: boolean;
  participantsInvitedToStage: number[];
  participantsWithRaisedHands: number[];
  participantsDisconnected: number[];
  showAudienceList: boolean;
  activePanelTab: number;
  isWaitingForNattering: boolean;
  /** Skipped Nattering insights */
  questionsAssignments: QuestionAssignmentWSPayload[];
  activeSpeakersIds: number[];
  roomIdToRate: string | null;
  eventPhase: EventPhase;
  natteringPhase: NatteringPhase;
  natteringStatus: NatteringStatus;
  rematchCountdown: { endsAt: number } | null;
  /**
   * When set, a `broadcastStarted_WS` arrived while the participant was still in the just-in-time
   * matching teleprompter (never matched). We buffer the broadcast mount for this countdown so the
   * teleprompter can play "Broadcast has resumed → returning in 3,2,1" before the broadcast appears.
   */
  natteringResumeCountdown: { endsAt: number } | null;
  /**
   * While true, the just-in-time skip confirmation ("Of course" + cancel) is open: the user has
   * left the matching pool but we hold the navigation to written feedback so they can still cancel
   * (re-accept). See `natteringActions.skipConfirm`.
   */
  natteringSkipConfirmActive: boolean;
  /**
   * True while the just-in-time matching teleprompter surface is actually mounted (the participant is
   * on the round screen that can play the connecting / broadcast-resume countdown). The socket
   * dispatcher only buffers a first-match join / broadcast-resume for its countdown when this is set —
   * otherwise (e.g. the host, who never mounts the teleprompter) the message dispatches immediately so
   * they go straight to the room / broadcast instead of waiting out the safety-fallback timeout.
   */
  isMatchingTeleprompterMounted: boolean;
}
